import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/Brand";
import { CheckCircle2, Sparkles, Compass, Heart } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=80";

const MAGDALENA_IMG =
    "https://customer-assets.emergentagent.com/job_biz-scorecard-1/artifacts/3lxmddoh_Magdalena%20Profile%20Photo.jpg";

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-white" data-testid="landing-page">
            <Header />

            {/* Hero */}
            <section className="w-full">
                <div className="px-6 sm:px-10 pt-6 pb-20 max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-7 fade-up">
                            <p className="overline mb-6">The Profitable-But-Broke Reality Check</p>
                            <h1 className="font-display uppercase text-[40px] sm:text-6xl lg:text-7xl xl:text-[78px] leading-[0.95] text-black">
                                Are you making money but still feel <span className="bg-pm-yellow px-2 inline-block">broke?</span> Find out in 5 minutes.
                            </h1>
                            <p className="mt-6 text-lg text-black/80 leading-relaxed max-w-xl">
                                You're running a real business. Clients are coming. Revenue looks decent.
                                But something doesn't add up — the bank balance doesn't match the profit
                                you've paid tax on, the sleep doesn't match the success, and the calm you
                                expected still hasn't arrived.
                            </p>
                            <p className="mt-4 text-lg text-black/80 leading-relaxed max-w-xl">
                                You might be <strong>Profitable But Broke</strong>. This free 5-minute
                                reality check diagnoses exactly where you sit on the spectrum — and tells you
                                which part of the <strong>Follow The Cash™</strong> method you need most.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-4">
                                <Link to="/assessment" data-testid="hero-cta-start" className="btn-pm">
                                    Start the Reality Check <span>→</span>
                                </Link>
                                <a href="#how-it-works" data-testid="hero-cta-learn" className="btn-pm-secondary">
                                    How it works
                                </a>
                            </div>
                            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-black/70">
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> 5 minutes</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> 100% free</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Personalised diagnosis</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> No spam, ever</li>
                            </ul>
                            <p className="mt-8 text-xs text-black/50 uppercase tracking-widest">Built by ProfitMIND · Follow The Cash™ Method</p>
                        </div>

                        <div className="lg:col-span-5 fade-up-d1">
                            <div className="relative">
                                <div className="absolute -top-3 -left-3 w-full h-full bg-pm-yellow rounded-3xl" aria-hidden />
                                <img
                                    src={HERO_IMG}
                                    alt="Purpose-led business owner reviewing her numbers calmly"
                                    className="relative rounded-3xl object-cover w-full h-[440px] sm:h-[520px] border-2 border-black"
                                    data-testid="hero-image"
                                />
                                <div className="absolute -bottom-5 -right-3 sm:-right-6 bg-white border-2 border-black rounded-2xl p-5 max-w-[240px]">
                                    <p className="overline mb-1">Your Reality Check</p>
                                    <p className="font-display text-2xl text-black leading-tight uppercase">Profitable But Broke</p>
                                    <p className="text-sm text-black/70 mt-1 italic">One possible diagnosis — yours might be different.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Marquee */}
            <section className="w-full bg-white border-y-2 border-black py-5 overflow-hidden" data-testid="mantra-marquee">
                <div className="flex marquee whitespace-nowrap font-display normal-case text-2xl sm:text-3xl text-black items-center">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <span key={i} className="inline-flex items-center">
                            <span className="px-10">Profitable But Broke.</span>
                            <span className="text-pm-yellow text-3xl sm:text-4xl">|</span>
                            <span className="px-10">Follow The Cash.</span>
                            <span className="text-pm-yellow text-3xl sm:text-4xl">|</span>
                            <span className="px-10">Own Your Future.</span>
                            <span className="text-pm-yellow text-3xl sm:text-4xl">|</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section id="how-it-works" className="w-full py-20 bg-black text-white">
                <div className="px-6 sm:px-10 max-w-6xl mx-auto">
                    <div className="max-w-2xl mb-14">
                        <p className="overline text-pm-yellow mb-3">How it works</p>
                        <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
                            Three small steps.<br />
                            <span className="text-pm-yellow">One clearer picture.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Sparkles,
                                title: "Answer 16 honest questions",
                                desc: "Multiple-choice questions across the four parts of Follow The Cash™. No judgement. No perfect numbers required. Just honesty about where you are right now.",
                                n: "01",
                            },
                            {
                                icon: Compass,
                                title: "Get your Reality Check",
                                desc: "Your answers place you on the Profitable-But-Broke spectrum with a clear diagnosis. You'll see exactly which part of the method is your biggest gap.",
                                n: "02",
                            },
                            {
                                icon: Heart,
                                title: "Receive your report",
                                desc: "A personalised report lands in your inbox with your diagnosis, per-part breakdown, three practical next moves and one recommended step — mapped to the right tool or offer.",
                                n: "03",
                            },
                        ].map((step, i) => (
                            <div key={i} data-testid={`step-card-${i}`} className="bg-white text-black rounded-2xl border-2 border-pm-yellow p-8">
                                <p className="font-display text-3xl text-pm-yellow-deep mb-4">{step.n}</p>
                                <step.icon className="w-7 h-7 text-black mb-4" />
                                <h3 className="font-display uppercase text-2xl text-black mb-2">{step.title}</h3>
                                <p className="text-black/75 leading-relaxed">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* The four parts of Follow The Cash™ */}
            <section className="w-full py-20">
                <div className="px-6 sm:px-10 max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-5">
                            <p className="overline mb-3">What we measure</p>
                            <h2 className="font-display uppercase text-4xl sm:text-5xl text-black leading-[0.95]">
                                Four parts.<br />
                                <span className="bg-pm-yellow px-2 inline-block mt-1">One journey.</span>
                            </h2>
                            <p className="mt-6 text-lg text-black/75 leading-relaxed">
                                Based on the <strong>Follow The Cash™</strong> method — a simple four-part framework
                                that moves you from avoidance and anxiety to evidence and calm.
                            </p>
                            <p className="mt-4 text-base text-black/65 italic">
                                &quot;It&apos;s not that you&apos;re bad with money. It&apos;s that no one&apos;s given you a method for seeing clearly.&quot;
                            </p>
                            <p className="mt-6">
                                <a
                                    href="https://profitmind.co.uk/follow-the-cash"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="landing-follow-the-cash-link"
                                    className="text-black underline font-bold"
                                >
                                    Explore the Follow The Cash™ method →
                                </a>
                            </p>
                        </div>
                        <ul className="md:col-span-7 space-y-3" data-testid="parts-list">
                            {[
                                ["01", "Stop Hiding", "Are you avoiding your numbers? How often do you actually look at your financial picture? What happens when you see something you don't like?"],
                                ["02", "Start Knowing", "Do you understand what your numbers mean? Do you know the difference between profit and cash? Could you name your profit margin on your main offer?"],
                                ["03", "Take Control", "Do you have a 12-month cashflow forecast? Do you check it before making big decisions like hiring, launching or investing?"],
                                ["04", "Own Your Future", "Are you confident and resilient? How do you feel about your business finances at 11pm? Do you have a cash buffer for quieter months?"],
                            ].map(([n, t, d], i) => (
                                <li key={i} className="flex items-start gap-5 p-6 rounded-2xl bg-white border-2 border-black">
                                    <span className="font-display text-3xl text-pm-yellow-deep flex-shrink-0">{n}</span>
                                    <div>
                                        <p className="font-display uppercase text-xl text-black">Part {n} — {t}</p>
                                        <p className="text-sm text-black/70 mt-1">{d}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* For who */}
            <section className="w-full py-20 bg-pm-yellow">
                <div className="px-6 sm:px-10 max-w-5xl mx-auto">
                    <p className="overline mb-3">Who this is for</p>
                    <h2 className="font-display uppercase text-4xl sm:text-5xl text-black leading-[0.95]">
                        For business owners quietly making the confession most won't say out loud.
                    </h2>
                    <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-3">
                        {[
                            "You've built something real, but privately the money still feels heavier than it should.",
                            "You check the bank balance for reassurance instead of reading a forecast.",
                            "You did your taxes, saw profit on paper — and wondered where the cash went.",
                            "You're stuck between okay and panicked, depending on what just hit the bank.",
                            "You want to pay yourself with more consistency and calm.",
                            "You'd like a gentle first step before booking a session or joining software.",
                        ].map((line, i) => (
                            <p key={i} className="flex items-start gap-3 text-black text-base leading-relaxed">
                                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" /> {line}
                            </p>
                        ))}
                    </div>
                </div>
            </section>

            {/* Meet Magdalena — unique short version */}
            <section className="w-full py-24" data-testid="about-magdalena">
                <div className="px-6 sm:px-10 max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="md:col-span-5">
                            <div className="relative">
                                <div className="absolute -top-3 -left-3 w-full h-full bg-pm-yellow rounded-3xl" aria-hidden />
                                <img
                                    src={MAGDALENA_IMG}
                                    alt="Magdalena Mahdy, founder of ProfitMIND and forecastly"
                                    className="relative rounded-3xl object-cover w-full h-[440px] sm:h-[520px] border-2 border-black"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-7">
                            <p className="overline mb-3">Meet Magdalena</p>
                            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl text-black leading-[0.95]">
                                "I figured this out because <span className="bg-pm-yellow px-2 inline-block">I lived it too."</span>
                            </h2>
                            <div className="mt-6 space-y-4 text-base sm:text-lg text-black/80 leading-relaxed">
                                <p>
                                    Profit &amp; Cashflow Strategist and founder of <strong>ProfitMIND</strong> and
                                    <strong> forecastly</strong>. Before all of this, there was a seasonal wedding
                                    photography business, plenty of busy-and-quiet months, and the constant unease of
                                    not knowing what was really coming.
                                </p>
                                <p>
                                    I was <strong>Profitable But Broke</strong> before I had a name for it — invoicing
                                    well, but unable to sleep because the cash wasn't there. Or looking at my tax bill
                                    and thinking, "where has all that money gone?"
                                </p>
                                <p>
                                    Cashflow forecasting became the tool that finally let me breathe — but the
                                    spreadsheet itself was clunky, manual and unfriendly. So years later, after
                                    mentoring hundreds of founders and retraining in bookkeeping and tax, I built
                                    ProfitMIND to bring practical numbers, business strategy and honest conversation
                                    into one place. And I'm building <strong>forecastly</strong> — the cashflow tool I
                                    wish I'd had.
                                </p>
                                <p>
                                    My work isn't about turning you into a spreadsheet person. It's about helping you{" "}
                                    <strong>Follow The Cash™</strong> — so you can stop being Profitable But Broke and
                                    start owning your future.
                                </p>
                                <p className="font-bold italic">
                                    forecastly helps you see what's coming.
                                    <br />ProfitMIND helps you decide what to do next.
                                </p>
                                <p>
                                    <a href="https://profitmind.co.uk/about" target="_blank" rel="noopener noreferrer" className="text-black underline font-bold">
                                        Read the full story →
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="w-full py-20">
                <div className="px-6 sm:px-10 max-w-5xl mx-auto bg-black text-white rounded-3xl p-12 sm:p-16 text-center relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-pm-yellow" aria-hidden />
                    <div className="relative">
                        <p className="overline text-pm-yellow mb-4">Ready when you are</p>
                        <h2 className="font-display uppercase text-4xl sm:text-6xl text-white leading-[0.95]">
                            Stop hiding.<br />Start <span className="text-pm-yellow">seeing what needs your attention next.</span>
                        </h2>
                        <p className="mt-6 text-white/80 max-w-xl mx-auto text-lg">
                            Your free Profitable-But-Broke Reality Check is 5 minutes away.
                        </p>
                        <Link to="/assessment" data-testid="footer-cta-start" className="mt-8 inline-flex btn-pm">
                            Get your Reality Check <span>→</span>
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
